import { Box, Button, Slider, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import * as styles from './CatalogFilterBar.styles';

interface ThresholdFilterProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  /** The committed threshold, or undefined when unset (i.e. at `min`). */
  value: number | undefined;
  /** Emits the number while set, or `undefined` once reset to `min`. */
  onChange: (value: number | undefined) => void;
}

/**
 * Single-handle "minimum threshold" slider for a `threshold` FilterFieldConfig.
 * The track runs `min`..`max`; sitting at `min` means "no filter", so a value of
 * `min` commits `undefined` (dropping the key). Drag updates local state and
 * commits on release; Clear resets to `min`. Uses theme `primary` (no override).
 */
export const ThresholdFilter = ({
  label,
  min,
  max,
  step = 1,
  value,
  onChange,
}: ThresholdFilterProps) => {
  const committed = value ?? min;
  const [current, setCurrent] = useState<number>(committed);
  const lastCommittedRef = useRef<number>(committed);

  // Re-sync when the committed value changes externally (Clear all, reopen).
  useEffect(() => {
    const next = value ?? min;
    if (next !== lastCommittedRef.current) {
      lastCommittedRef.current = next;
      setCurrent(next);
    }
  }, [value, min]);

  const commit = (next: number) => {
    setCurrent(next);
    lastCommittedRef.current = next;
    onChange(next > min ? next : undefined);
  };

  return (
    <Box sx={styles.popoverContent}>
      <Box sx={styles.sliderBox}>
        <Slider
          value={current}
          min={min}
          max={max}
          step={step}
          valueLabelDisplay="auto"
          aria-label={label}
          onChange={(_, v) => setCurrent(v as number)}
          onChangeCommitted={(_, v) => commit(v as number)}
        />
        <Box sx={styles.sliderScale}>
          <Typography variant="caption">min</Typography>
          <Typography variant="caption">max</Typography>
        </Box>
      </Box>
      <Button
        variant="text"
        size="small"
        disabled={current <= min}
        onClick={() => commit(min)}
        sx={styles.clearButton}
      >
        Clear
      </Button>
    </Box>
  );
};
